/**
 * Discussions: the open questions and the decisions of record of Norican I2Q.
 *
 * Anyone signed in reads the register, opens a topic and comments under their own name
 * (the author is the session's e-mail; the server derives the shown name). A manager
 * sets the status (open, decided, parked) and writes the decision of record. Decided
 * topics stay visible. Seeded topics carry a `key`, which the roadmap uses to link an
 * item to its discussion.
 *
 * Business decisions (15 Oct 2026) are topics of the same register with `kind`
 * "business_decision" and a frame: the owner role, the milestone, the options with neutral
 * properties and what I2Q does today. A manager edits the frame (`PUT .../frame`) and
 * records the role that decided and the option picked. Roles, never persons.
 *
 * No React in here: the types, the limits, the query keys, the raw requests and the
 * wording of a failure. The hooks live in `hooks.ts`. The shapes come from the
 * generated client, so a backend change shows up at type-check time.
 */

import { type ApiError } from "./client";
import {
  addDiscussionComment,
  createDiscussion,
  decideDiscussion,
  type DecisionFrame as GenDecisionFrame,
  type DecisionOption as GenDecisionOption,
  type DiscussionComment as GenDiscussionComment,
  type DiscussionCreateRequest as GenDiscussionCreateRequest,
  type DiscussionDecisionRequest as GenDiscussionDecisionRequest,
  type DiscussionDetail as GenDiscussionDetail,
  type DiscussionList as GenDiscussionList,
  type DiscussionSummary as GenDiscussionSummary,
  getDiscussion,
  listDiscussions,
  setDecisionFrame,
} from "./generated";
import { detailText } from "./work-actions";

export type DiscussionSummary = GenDiscussionSummary;
export type DiscussionStatus = DiscussionSummary["status"];
export type DiscussionComment = GenDiscussionComment;
export type DiscussionList = GenDiscussionList;
export type DiscussionCreateRequest = GenDiscussionCreateRequest;
export type DiscussionDecisionRequest = GenDiscussionDecisionRequest;

/** A topic with its thread, oldest comment first. */
export type DiscussionDetail = GenDiscussionDetail;

/** "topic" or "business_decision" (a missing kind is a topic). */
export type DiscussionKind = NonNullable<DiscussionSummary["kind"]>;
/** The frame of a business decision; one option with its neutral properties. */
export type DecisionFrame = GenDecisionFrame;
export type DecisionOption = GenDecisionOption;

export const DISCUSSION_TITLE_MAX = 160;
export const DISCUSSION_BODY_MAX = 4000;
export const DISCUSSION_COMMENT_MAX = 2000;
export const DISCUSSION_DECISION_MAX = 2000;
/** The limits of a frame (`DecisionFrame` of `api/models.py`). */
export const DECISION_ROLE_MAX = 80;
export const DECISION_NEEDED_FOR_MAX = 80;
export const DECISION_DEFAULT_MAX = 1000;
export const DECISION_OPTION_TITLE_MAX = 160;
export const DECISION_PROPERTY_MAX = 300;
export const DECISION_OPTIONS_MIN = 2;
export const DECISION_OPTIONS_MAX = 6;
export const DECISION_PROPERTIES_MAX = 8;
export const DECISION_OPTION_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;

export const DISCUSSION_STATUSES: readonly DiscussionStatus[] = ["open", "decided", "parked"];

export const discussionKeys = {
  all: ["discussions"] as const,
  /** The register; with a kind, only that kind (`GET /discussions?kind=`). */
  list: (kind?: DiscussionKind) =>
    kind ? (["discussions", "list", kind] as const) : (["discussions", "list"] as const),
  detail: (id: number) => ["discussions", "detail", id] as const,
};

/** True for a topic that is a business decision with its frame. */
export function isBusinessDecision(item: Pick<DiscussionSummary, "kind">): boolean {
  return item.kind === "business_decision";
}

/** The result of a generated SDK call made with `throwOnError: false`. */
interface SdkResult<T> {
  data?: T;
  error?: unknown;
  response?: Response;
}

/** Unwrap a call, or throw `{ status, body }` like the feedback hub does. */
function unwrap<T>(result: SdkResult<T>): T {
  if (result.error !== undefined || !result.response?.ok) {
    const failure = (result.error ?? {}) as Partial<ApiError>;
    const thrown: ApiError = {
      status: failure.status ?? result.response?.status ?? 0,
      body: failure.body ?? null,
    };
    throw thrown;
  }
  return result.data as T;
}

/** `GET /discussions` (listDiscussions): open first, then decided, then parked. */
export async function listDiscussionsRaw(kind?: DiscussionKind): Promise<DiscussionList> {
  return unwrap(
    await listDiscussions(
      kind ? { query: { kind }, throwOnError: false } : { throwOnError: false },
    ),
  );
}

/** `POST /discussions` (createDiscussion). Throws a RangeError on an empty or long text. */
export async function createDiscussionRaw(
  request: DiscussionCreateRequest,
): Promise<DiscussionDetail> {
  const title = request.title.trim();
  const body = request.body.trim();
  if (title === "") {
    throw new RangeError("Give the topic a title.");
  }
  if (title.length > DISCUSSION_TITLE_MAX) {
    throw new RangeError(`The title has at most ${DISCUSSION_TITLE_MAX} characters.`);
  }
  if (body === "") {
    throw new RangeError("Write the question or proposal first.");
  }
  if (body.length > DISCUSSION_BODY_MAX) {
    throw new RangeError(`The text has at most ${DISCUSSION_BODY_MAX} characters.`);
  }
  const area = typeof request.area === "string" ? request.area.trim().toLowerCase() : "";
  return unwrap(
    await createDiscussion({
      body: { title, body, area: area === "" ? null : area },
      throwOnError: false,
    }),
  );
}

/** `GET /discussions/{id}` (getDiscussion). */
export async function getDiscussionRaw(discussionId: number): Promise<DiscussionDetail> {
  return unwrap(
    await getDiscussion({ path: { discussion_id: discussionId }, throwOnError: false }),
  );
}

/** `POST /discussions/{id}/comments` (addDiscussionComment). Throws a RangeError on a bad text. */
export async function addDiscussionCommentRaw(
  discussionId: number,
  text: string,
): Promise<DiscussionDetail> {
  const trimmed = text.trim();
  if (trimmed === "") {
    throw new RangeError("Write a few words first.");
  }
  if (trimmed.length > DISCUSSION_COMMENT_MAX) {
    throw new RangeError(`At most ${DISCUSSION_COMMENT_MAX} characters.`);
  }
  return unwrap(
    await addDiscussionComment({
      path: { discussion_id: discussionId },
      body: { text: trimmed },
      throwOnError: false,
    }),
  );
}

/** `PATCH /discussions/{id}` (decideDiscussion), managers only. */
export async function decideDiscussionRaw(
  discussionId: number,
  request: DiscussionDecisionRequest,
): Promise<DiscussionDetail> {
  const decision = typeof request.decision === "string" ? request.decision.trim() : "";
  if (request.status === "decided" && decision === "") {
    throw new RangeError("A decided topic needs a decision text.");
  }
  if (decision.length > DISCUSSION_DECISION_MAX) {
    throw new RangeError(`At most ${DISCUSSION_DECISION_MAX} characters.`);
  }
  const role = typeof request.decided_by_role === "string" ? request.decided_by_role.trim() : "";
  if (role.includes("@")) {
    throw new RangeError("Write the role, not the name of a person.");
  }
  if (role.length > DECISION_ROLE_MAX) {
    throw new RangeError(`The role has at most ${DECISION_ROLE_MAX} characters.`);
  }
  const option = typeof request.chosen_option === "string" ? request.chosen_option : "";
  return unwrap(
    await decideDiscussion({
      path: { discussion_id: discussionId },
      body: {
        status: request.status,
        decision: decision === "" ? null : decision,
        // Only a business decision carries these two; a topic sends neither.
        ...(request.decided_by_role !== undefined ? { decided_by_role: role || null } : {}),
        ...(request.chosen_option !== undefined ? { chosen_option: option || null } : {}),
      },
      throwOnError: false,
    }),
  );
}

/** What is wrong with a frame before it is sent, or null when it may go. */
export function frameProblem(frame: DecisionFrame): string | null {
  const role = frame.owner_role.trim();
  if (role === "" || frame.needed_for.trim() === "" || frame.current_default.trim() === "") {
    return "Give an owner, a milestone and what I2Q does today.";
  }
  if (role.includes("@")) {
    return "Write the role, not the name of a person.";
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(frame.needed_by)) {
    return "Give the date of the milestone.";
  }
  if (
    role.length > DECISION_ROLE_MAX ||
    frame.needed_for.trim().length > DECISION_NEEDED_FOR_MAX ||
    frame.current_default.trim().length > DECISION_DEFAULT_MAX
  ) {
    return "A field is too long.";
  }
  const options = frame.options;
  if (options.length < DECISION_OPTIONS_MIN || options.length > DECISION_OPTIONS_MAX) {
    return `Give ${DECISION_OPTIONS_MIN} to ${DECISION_OPTIONS_MAX} options.`;
  }
  const keys = new Set<string>();
  for (const entry of options) {
    if (!DECISION_OPTION_PATTERN.test(entry.key) || keys.has(entry.key)) {
      return "Each option needs its own title.";
    }
    keys.add(entry.key);
    const title = entry.title.trim();
    if (title === "" || title.length > DECISION_OPTION_TITLE_MAX) {
      return "Each option needs a title.";
    }
    const properties = entry.properties.map((value) => value.trim()).filter(Boolean);
    if (properties.length < 1 || properties.length > DECISION_PROPERTIES_MAX) {
      return `Give each option 1 to ${DECISION_PROPERTIES_MAX} properties.`;
    }
    if (properties.some((value) => value.length > DECISION_PROPERTY_MAX)) {
      return `A property has at most ${DECISION_PROPERTY_MAX} characters.`;
    }
  }
  return null;
}

/**
 * The short key of an option from its title: "One-country pilot" is "one-country-pilot".
 * A title without a letter gets "option"; a key that is taken gets a number.
 */
export function optionKeyOf(title: string, taken: ReadonlySet<string> = new Set()): string {
  const base =
    title
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^[^a-z]+/, "")
      .replace(/-+$/, "")
      .slice(0, 28)
      .replace(/-+$/, "") || "option";
  let key = base;
  for (let n = 2; taken.has(key); n += 1) {
    key = `${base}-${n}`;
  }
  return key;
}

/** `PUT /discussions/{id}/frame` (setDecisionFrame), managers only. Trims every text. */
export async function setDecisionFrameRaw(
  discussionId: number,
  frame: DecisionFrame,
): Promise<DiscussionDetail> {
  const clean: DecisionFrame = {
    owner_role: frame.owner_role.trim(),
    needed_for: frame.needed_for.trim(),
    needed_by: frame.needed_by,
    current_default: frame.current_default.trim(),
    options: frame.options.map((entry) => ({
      key: entry.key,
      title: entry.title.trim(),
      properties: entry.properties.map((value) => value.trim()).filter(Boolean),
    })),
  };
  const problem = frameProblem(clean);
  if (problem) {
    throw new RangeError(problem);
  }
  return unwrap(
    await setDecisionFrame({
      path: { discussion_id: discussionId },
      body: clean,
      throwOnError: false,
    }),
  );
}

/** What a failed discussion request reads as. Raw server text of a 5xx never reaches the screen. */
export function discussionFailureText(
  error: unknown,
  what: "create" | "comment" | "decide" | "frame" | "read" = "read",
): string {
  if (error instanceof RangeError) {
    return error.message;
  }
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (status === 422) {
    return detailText(body) ?? "The server did not accept this. Check the fields and try again.";
  }
  if (status === 403) {
    return what === "decide"
      ? "Only a manager can set the status or the decision."
      : what === "frame"
        ? "Only a manager can edit the owner, the date and the options."
        : "Your role does not allow this.";
  }
  if (status === 404) {
    return "This topic does not exist.";
  }
  if (status === 409 && what === "frame") {
    return "This decision is decided. Set it back to open to change the owner, date or options.";
  }
  if (status === 401) {
    return "Your session has ended. Sign in again.";
  }
  if (status === 503) {
    return "Discussions are not available right now. Try again later.";
  }
  return what === "read"
    ? "This could not be loaded. Try again in a moment."
    : "This could not be saved. Try again in a moment.";
}
